import { all, call, put, select, takeEvery } from "redux-saga/effects";

import { ConditionParams } from "@multiweb/clients/dataReader";
import { bridge } from "@multiweb/reactCore/bridge";
import { IRunMethodResult } from "@multiweb/types/contracts";
import { IOWObject } from "@multiweb/types/multiweb";
import { SagaWithReturnType } from "@multiweb/types/react";
import { createSetLoadingAction } from "./actions";
import { CLICK } from "./constants";
import { getLinkButtonInstance } from "./selectors";
import { ILinkButtonClickAction } from "./types";
import { processUrl } from "./utils";

const { runStaticMethodWithParams, isNullOrEmpty, errorHandler, openUrl, getInstance, isParametricUrl, hasParameters, canCallOnlyStaticMethods, createDefferedWindow } = bridge;

export const saga: SagaWithReturnType = function* linkButtonSaga() {
    yield takeEvery([CLICK], clickSaga);
};

export const runFunctionSaga: SagaWithReturnType<[string, IOWObject | null], Promise<IRunMethodResult> | undefined> = function* runFunctionSaga(methodName, actualObject) {
    if (isNullOrEmpty(methodName)) {
        call(errorHandler, { name: "Empty function name.", message: "Empty function name." });
        return undefined;
    } else {
        const separatorPosition = methodName.indexOf(".");
        if (separatorPosition > 0) {
            // Volání statické metody
            return yield call(runStaticMethodWithParams, { className: methodName.substring(0, separatorPosition), methodName: methodName.substring(separatorPosition + 1) });
        } else if (actualObject) {
            // Volání instanční metody - je nutné předávat params s prázdným polem, jinak je výsledkem null
            return yield call([actualObject, "runWithParams"], { methodName, params: [] });
        }
    }
};

export const getParametersSaga: SagaWithReturnType<
[IOWObject, readonly string[]], Promise<ConditionParams> | undefined> = function* getParametersSaga(actualObject, params) {
    return yield call([actualObject, "getParameters"], params);
};

export const manageUrlSaga: SagaWithReturnType<
    [readonly string[], readonly string[], string, string, IOWObject | null],
    void
> = function* manageUrlSaga(params, functions, dataKey, url, actualObject) {
    try {
        // Kvůli vlastnosti blokování otevírání stránek z aync callu Safari, je nutné vytvořit si window dříve a poté změnit location
        const deffered = createDefferedWindow();
        yield put(createSetLoadingAction(dataKey, true));
        let paramResults: string[] = [];
        if (actualObject)
            paramResults = yield call(getParametersSaga, actualObject, params);

        const results = yield all(functions.map(functionName => call(runFunctionSaga, functionName, actualObject)));
        const processedUrl: ReturnType<typeof processUrl> = yield call(processUrl, url, paramResults, results);
        yield call([deffered, "navigate"], processedUrl);
    } catch (error) {
        yield call(errorHandler, (error as Error));
    } finally {
        yield put(createSetLoadingAction(dataKey, false));
    }
};

/**
 * Otevře zpracovanou url
 * @param action
 */
export const clickSaga: SagaWithReturnType<[ILinkButtonClickAction]> = function* clickSaga(action) {
    const linkButtonInstance: ReturnType<typeof getLinkButtonInstance> = yield select(
        getLinkButtonInstance,
        action.meta.dataKey,
    );

    if (!linkButtonInstance) {
        yield call(errorHandler, {
            name: "LinkButton instance not found",
            message: `LinkButton instance with dataKey '${action.meta.dataKey}' not found.`,
        });
        return;
    }

    const { url, functions, params, instanceID } = linkButtonInstance;

    if (!(yield call(isParametricUrl, url))) {
        yield call(errorHandler, {
            name: "Url is not valid.",
            message: "Given url is not valid.",
        });
        return;
    }

    // bez parametrů
    if (!(yield call(hasParameters, url))) {
        yield call(openUrl, url);
        return;
    }

    // pouze se statickými metodami
    if (yield call(canCallOnlyStaticMethods, url, functions)) {
        yield call(manageUrlSaga, params, functions, action.meta.dataKey, url, null);
        return;
    }

    if (!instanceID) {
        yield call(errorHandler, {
            name: "actualObject is not selected.",
            message: "Cannot exectute action - actualObject is not selected.",
        });
        return;
    }

    const actualObject: ReturnType<typeof getInstance> = yield call(getInstance, instanceID);

    if (!actualObject) {
        yield call(errorHandler, {
            name: "actualObject is not selected.",
            message: "Cannot exectute action - actualObject is not selected.",
        });
        return;
    }

    if (!actualObject.classInfo?.className) {
        yield call(errorHandler, { name: "ClassName is empty.", message: "Cannot call given functions - className is empty." });
        return;
    }

    yield call(manageUrlSaga, params, functions, action.meta.dataKey, url, actualObject);
};
